import { Link } from 'react-router-dom';

const columns = [
  { label: 'AdForge AI', highlight: true },
  { label: 'ChatGPT / Generic AI', highlight: false },
  { label: 'Freelance Copywriter', highlight: false },
  { label: 'Marketing Agency', highlight: false },
];

type Row = { feature: string; values: (boolean | string)[] };

const rows: Row[] = [
  { feature: 'Ad copy generation', values: [true, true, true, true] },
  { feature: 'AI-generated ad images', values: [true, false, false, true] },
  { feature: 'Built-in visual editor', values: [true, false, false, false] },
  { feature: 'Platform-optimized formatting', values: [true, false, false, true] },
  { feature: 'A/B variation generation', values: ['Up to 20', 'Manual', 'Extra cost', 'Extra cost'] },
  { feature: 'Business profile memory', values: [true, false, false, true] },
  { feature: 'Ready in under 3 minutes', values: [true, false, false, false] },
  { feature: 'Ad history & library', values: [true, false, false, 'Extra cost'] },
  { feature: 'No experience required', values: [true, false, false, true] },
  { feature: 'Monthly cost', values: ['From $0', 'From $20', '$300–$800+', '$1,000–$5,000+'] },
];

function Cell({ value, highlight }: { value: boolean | string; highlight: boolean }) {
  if (typeof value === 'boolean') {
    return (
      <div className="flex justify-center">
        {value ? (
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-check-line text-base font-bold ${highlight ? 'text-orange-500' : 'text-emerald-500'}`} />
          </div>
        ) : (
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-close-line text-base text-stone-300" />
          </div>
        )}
      </div>
    );
  }
  return (
    <span className={`text-xs font-semibold ${highlight ? 'text-orange-600' : 'text-stone-500'}`}>
      {value}
    </span>
  );
}

export default function FeaturesComparison() {
  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">
            Comparison
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            AdForge vs Everything Else
          </h2>
          <p className="text-stone-500 text-base leading-relaxed">
            See how AdForge stacks up against using ChatGPT, hiring a copywriter, or paying for
            a full marketing agency — on features and cost.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-stone-100">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-stone-100">
                <th className="text-left text-xs font-bold text-stone-400 uppercase tracking-wider px-6 py-4 w-48 lg:w-64">
                  Feature
                </th>
                {columns.map((col) => (
                  <th
                    key={col.label}
                    className={`text-center text-xs font-bold uppercase tracking-wide px-4 py-4 ${
                      col.highlight
                        ? 'bg-orange-50 text-orange-600'
                        : 'text-stone-400'
                    }`}
                  >
                    {col.highlight && (
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <i className="ri-star-fill text-orange-400 text-xs" />
                        <span className="text-orange-500 text-xs normal-case font-semibold">Best Value</span>
                      </div>
                    )}
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.feature}
                  className={`border-b border-stone-50 ${i % 2 === 0 ? 'bg-white' : 'bg-stone-50/40'}`}
                >
                  <td className="text-sm text-stone-700 font-medium px-6 py-4">{row.feature}</td>
                  {row.values.map((v, j) => (
                    <td
                      key={j}
                      className={`text-center px-4 py-4 ${columns[j].highlight ? 'bg-orange-50/50' : ''}`}
                    >
                      <Cell value={v} highlight={columns[j].highlight} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-stone-500 mb-5">
            Ready to see why AdForge is the smarter choice for small businesses and contractors?
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Start Free — No Credit Card
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
